import createTxpDataRow from './createTxpDataRow';
import convertDateTimeToString from './convertDateTimeToString';
import populateLodgingBusStopData from './populateLodgingBusStopData';
import ActivityType from '../../schema/innerSchema/ActivityType';
import BusStopData from '../../schema/viewSchema/BusStopData';
import CardItem from '../../schema/viewSchema/CardItem';
import CardRole from '../../schema/innerSchema/CardRole';
import DetailsIcons from '../../schema/configSchema/DetailsIcons';
import EntityType from '../../schema/innerSchema/EntityType';
import LodgingReservation from '../../schema/LodgingReservation';
import ActionNameConstants from '../../helpers/ActionNameConstants';
import TxpDataRow from '../../schema/viewSchema/TxpDataRow';
import TxpStrings from '../../schema/configSchema/TxpStrings';
import PostalAddress from '../../schema/innerSchema/PostalAddress';
import TxpDateTimeCallbacks from 'txp-data/lib/schema/configSchema/TxpDateTimeCallbacks';

/**
 *  Extracts the lodging specific data needed for displaying the card
 */
export default function createLodgingCard(
    lodgingEntity: LodgingReservation,
    isBusStop: boolean,
    activityType: ActivityType,
    activityId: string,
    showLocation: boolean,
    entityImage: string,
    txpStrings: TxpStrings,
    entityIcon?: string,
    cardRole?: CardRole,
    calendarEventId?: string,
    detailsIcons?: DetailsIcons,
    txpDateTimeCallbacks?: TxpDateTimeCallbacks
): CardItem {
    let checkInTimeText: string;
    let checkOutTimeText: string;
    let detailsCheckOutLine: TxpDataRow;
    let busStopData: BusStopData;
    let hotelDetails = lodgingEntity.lodgingBusiness;
    let hotelName;
    let hotelAddress: PostalAddress;
    let hotelTelephone: string;

    if (hotelDetails) {
        hotelName = hotelDetails.name;
        hotelAddress = hotelDetails.address;
        hotelTelephone = hotelDetails.telephone;
    }

    let locationText = hotelDetails && hotelAddress ? hotelAddress.streetAddress : null;
    let summarySecondLine =
        lodgingEntity.reservationId &&
        txpStrings.formatters.format(
            txpStrings.strings.reservationCodeText,
            lodgingEntity.reservationId
        );

    let parsedCheckOutTimeText = convertDateTimeToString(
        lodgingEntity.checkoutTime,
        false /* isAccessible */,
        txpStrings.formatters,
        txpDateTimeCallbacks
    );
    let parsedCheckInTimeText = convertDateTimeToString(
        lodgingEntity.checkinTime,
        false /* isAccessible */,
        txpStrings.formatters,
        txpDateTimeCallbacks
    );

    const actionButtonData = lodgingEntity.modifyReservationUrl
        ? {
              actionIconName: detailsIcons.edit,
              actionUrl: lodgingEntity.modifyReservationUrl,
              actionButtonText: txpStrings.strings.modifyReservationText,
              actionName: ActionNameConstants.ModifyReservation,
          }
        : null;

    if (cardRole == CardRole.Combined) {
        checkInTimeText = parsedCheckInTimeText;
        checkOutTimeText = parsedCheckOutTimeText;
        detailsCheckOutLine = createTxpDataRow(
            checkOutTimeText,
            txpStrings.strings.hotelCheckOutText,
            detailsIcons.clock
        );
    } else if (activityType == ActivityType.HotelCheckout) {
        checkOutTimeText = parsedCheckOutTimeText;
        detailsCheckOutLine = createTxpDataRow(
            checkOutTimeText,
            txpStrings.strings.hotelCheckOutText,
            detailsIcons.clock
        );
    } else if (activityType == ActivityType.HotelCheckin) {
        checkInTimeText = parsedCheckInTimeText;
    }
    if (isBusStop) {
        busStopData = populateLodgingBusStopData(
            lodgingEntity,
            activityType,
            txpStrings,
            detailsIcons.hotel
        );
    }
    locationText = showLocation ? locationText : null;

    return {
        id: activityId,
        summaryData: {
            firstLine: createTxpDataRow(
                hotelDetails.name,
                null /* label */,
                null /* icon */,
                true /* isRequired */,
                txpStrings.strings.placeHolderForHotelName
            ),
            secondLine: {
                text: summarySecondLine,
                icon: detailsIcons.hotel,
            },
        },
        entityIcon: entityIcon,
        entityImage: entityImage,
        activityType: activityType,
        entityType: EntityType.LodgingReservation,
        busStopData: busStopData,
        editableField: hotelName,
        detailsData: {
            firstLine: createTxpDataRow(
                checkInTimeText,
                txpStrings.strings.hotelCheckInText,
                detailsIcons.clock
            ),
            secondLine: detailsCheckOutLine,
            thirdLine: hotelTelephone
                ? createTxpDataRow(
                      hotelTelephone,
                      txpStrings.strings.reservationPhoneText,
                      detailsIcons.phone
                  )
                : null,
            fourthLine: createTxpDataRow(
                locationText,
                null /* label */,
                detailsIcons.location,
                true /* is required */,
                txpStrings.strings.placeHolderForLocation,
                true /* includeToolTip */
            ),
        },
        brandsSummaryFirstLine: hotelDetails.name,
        brandsSummarySecondLine: txpStrings.formatters.formatWeekDayDateTime(
            lodgingEntity.checkinTime
        ),
        actionButtonData: actionButtonData,
        calendarEventId: calendarEventId,
        inferenceId: lodgingEntity.inferenceId,
        postalAddress: hotelAddress,
    } as CardItem;
}
